import { getIdentifier } from '@medplum/core'
import type { Identifier, Resource } from '@medplum/fhirtypes'
import { trimEnd } from 'lodash-es'
import {
  ELATION_SANDBOX_ID_SYSTEM,
  identifierTypePlacer,
} from '#src/codeSystems.ts'
import {
  ELATION_FEED_ITEM_ID_QUERY_PARAM,
  SEEN_ACTION_DRAFT_ID_QUERY_PARAM,
  SEEN_ACTION_OPEN_UNSIGNED_DRAFT_QUERY_PARAM,
  SEEN_ACTION_PHARMACY_NAME_QUERY_PARAM,
  SEEN_ACTION_PHARMACY_QUERY_PARAM,
  SEEN_ACTION_QUERY_PARAM,
} from '#src/constants.ts'
import type { DraftPharmacy } from '#src/elationDraftPharmacy.ts'

/**
 * Builds the URL for starting an Elation note
 * @param options - An object containing the base URL, patient ID, and appointment ID for the Elation instance
 * @param options.elationBaseUrl - The base URL for the Elation instance
 * @param options.elationPatientId - The Elation patient ID
 * @param options.elationAppointmentId - The Elation appointment ID
 * @returns The URL for starting an Elation note
 */
export const buildElationUrlNoteStart = ({
  elationBaseUrl,
  elationPatientId,
  elationAppointmentId,
}: {
  elationBaseUrl: string
  elationPatientId: number | string
  elationAppointmentId: number | string
}): string =>
  `${buildElationUrlPatient({ elationBaseUrl, elationPatientId })}/vnote?appointment_id=${elationAppointmentId}`

export const buildElationUrlFeedItem = ({
  elationBaseUrl,
  elationPatientId,
  elationFeedItemId,
}: {
  elationBaseUrl: string
  elationPatientId: number | string
  elationFeedItemId?: number | string
}): string =>
  `${buildElationUrlPatient({ elationBaseUrl, elationPatientId })}?${ELATION_FEED_ITEM_ID_QUERY_PARAM}=${elationFeedItemId}`

export const buildElationUrlPatient = ({
  elationBaseUrl,
  elationPatientId,
}: {
  elationBaseUrl: string
  elationPatientId: number | string
}): string => `${trimEnd(elationBaseUrl, '/')}/patient/${elationPatientId}`

export const buildElationUrlPrescribe = ({
  elationBaseUrl,
  elationPatientId,
}: {
  elationBaseUrl: string
  elationPatientId: number | string
}): string =>
  `${buildElationUrlPatient({ elationBaseUrl, elationPatientId })}?${SEEN_ACTION_QUERY_PARAM}=prescribe`

export const buildElationUrlDraftPrescription = ({
  elationBaseUrl,
  elationPatientId,
  draftId,
  pharmacy,
  openUnsignedDraft,
}: {
  elationBaseUrl: string
  elationPatientId: number | string
  draftId: number | string
  pharmacy?: DraftPharmacy
  openUnsignedDraft?: boolean
}): string => {
  const url = new URL(
    buildElationUrlPatient({ elationBaseUrl, elationPatientId })
  )
  url.searchParams.set(SEEN_ACTION_QUERY_PARAM, 'draft-prescription')
  url.searchParams.set(SEEN_ACTION_DRAFT_ID_QUERY_PARAM, String(draftId))
  if (openUnsignedDraft) {
    url.searchParams.set(SEEN_ACTION_OPEN_UNSIGNED_DRAFT_QUERY_PARAM, '1')
  }
  if (pharmacy) {
    url.searchParams.set(SEEN_ACTION_PHARMACY_QUERY_PARAM, pharmacy.searchText)
    url.searchParams.set(
      SEEN_ACTION_PHARMACY_NAME_QUERY_PARAM,
      pharmacy.storeName
    )
  }
  return url.toString()
}

export function extractElationId(resource: Resource): number | undefined {
  const identifier = getIdentifier(resource, ELATION_SANDBOX_ID_SYSTEM)
  return identifier !== undefined ? Number(identifier) : undefined
}

export function elationSandboxIdentifier(id: string | undefined): Identifier {
  return {
    system: ELATION_SANDBOX_ID_SYSTEM,
    value: id,
    type: identifierTypePlacer,
  }
}

export function elationSandboxIdentifierString(id: string | undefined): string {
  return `${ELATION_SANDBOX_ID_SYSTEM}|${id}`
}
